'use client';

import { Box, Center, Text, VStack, Spinner, Skeleton } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { memo, useEffect, useState, FC } from 'react';
import { DynamicOptionsLoadingProps } from 'next/dynamic';

const MemoizedText = memo(Text);
const MemoizedBox = memo(Box);

const spin = keyframes`
  0% { transform: rotateX(0deg) rotateY(0deg); }
  50% { transform: rotateX(180deg) rotateY(180deg); }
  100% { transform: rotateX(360deg) rotateY(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

type LoaderProps = {
  text?: string;
  size?: string | number;
  isFullscreen?: boolean;
};

type CubeFaceProps = {
  index: number;
  transform: string;
  opacity: number;
  size: string | number;
};

interface CubeFaceConfig {
  transform: string;
  opacity: number;
}

const CubeFace = memo(({ index, transform, opacity, size }: CubeFaceProps) => (
  <MemoizedBox
    key={index}
    position="absolute"
    top="0"
    left="0"
    width="100%"
    height="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    transform={transform}
    border="1px solid white"
    opacity={opacity}
    bg="red-wine"
  >
    <img
      loading="eager"
      src="/images/logos/aajumpLogo.webp"
      alt="Logo"
      style={{
        maxWidth: "80%",
        maxHeight: "80%",
        objectFit: "cover",
      }}
    />
  </MemoizedBox>
));

const createCubeFaces = (cubeSize: string | number): CubeFaceConfig[] => {
  const halfSize = parseInt(typeof cubeSize === 'string' ? cubeSize : `${cubeSize}`) / 2;
  return [
    { transform: `translateZ(${halfSize}px)`, opacity: 0.9 },
    { transform: `translateZ(-${halfSize}px) rotateY(180deg)`, opacity: 0.8 },
    { transform: `rotateY(-90deg) translateZ(${halfSize}px)`, opacity: 0.7 },
    { transform: `rotateY(90deg) translateZ(${halfSize}px)`, opacity: 0.7 },
    { transform: `rotateX(90deg) translateZ(${halfSize}px)`, opacity: 0.6 },
    { transform: `rotateX(-90deg) translateZ(${halfSize}px)`, opacity: 0.6 },
  ];
};

export const Loader3D = memo(({
  text = "Carregando...",
  size = "100px",
  isFullscreen = false,
}: LoaderProps) => {
  const cubeSize = typeof size === 'number' ? `${size}px` : size;
  const cubeFaces = createCubeFaces(cubeSize);

  const cubeContent = (
    <>
      <MemoizedBox
        position="relative"
        width={cubeSize}
        height={cubeSize}
        transformStyle="preserve-3d"
        animation={`${spin} 3s infinite ease-in-out`}
      >
        {cubeFaces.map((face, index) => (
          <CubeFace 
            key={index}
            index={index}
            transform={face.transform}
            opacity={face.opacity}
            size={cubeSize}
          />
        ))}
      </MemoizedBox>

      {text && (
        <MemoizedText
          fontSize="xl"
          fontWeight="bold"
          color="gray.800"
          mt={6}
          animation={`${pulse} 1.5s infinite ease-in-out`}
        >
          {text}
        </MemoizedText>
      )}
    </>
  );

  if (isFullscreen) {
    return (
      <MemoizedBox
        position="fixed"
        inset={0}
        bg="whiteAlpha.800"
        zIndex={9999}
        backdropFilter="blur(3px)"
        pointerEvents="all"
      >
        <Center h="100%">
          <VStack>
            {cubeContent}
          </VStack>
        </Center>
      </MemoizedBox>
    );
  }

  return (
    <VStack align="center">
      {cubeContent}
    </VStack>
  );
});

export default function LoadingOverlay({ text = 'Carregando...' }: LoaderProps) {
  const [clientMounted, setClientMounted] = useState(false);
  useEffect(() => {
    const checkMount = () => setClientMounted(true);
    
    if ('requestIdleCallback' in window) {
      const idleCallbackId = window.requestIdleCallback(checkMount);
      
      return () => {
        if ('cancelIdleCallback' in window) {
          window.cancelIdleCallback(idleCallbackId);
        }
      };
    } else {
      const timeoutId = setTimeout(checkMount, 0);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  if (clientMounted) return null;

  return <Loader3D text={text} isFullscreen size={70} />;
}

export const LoadingSpinner = memo(({
  text = "Carregando...",
}: LoaderProps) => (
  <VStack align="center">
    <Spinner color="red-wine" />
    <MemoizedText color="gray.700" fontSize="lg" fontWeight="bold">{text}</MemoizedText>
  </VStack>
));

export const skeletonLoader = (height: string) => (
  <Box p={4}>
    <Skeleton height={height} borderRadius="lg" />
  </Box>
);